import React from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageState } from "@/components/common/PageState";
import { AdminPartnerProviderConfiguration } from "@/types/admin-partner-provider";
import { PartnerProviderDialog } from "./components/PartnerProviderDialog";
import {
  usePartnerProviders,
  useSavePartnerProvider,
} from "./hooks/partner-providers.hooks";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { PartnerProviderFormValues } from "./utils/partnerProviderSchema";

const SUPPORTED_PARTNER_PROVIDERS = ["VIATOR"] as const;

const getAddButtonTitle = (
  noPermission: boolean,
  noProviders: boolean,
  t: ReturnType<typeof useTranslation>["t"],
): string | undefined => {
  if (noPermission) {
    return t("common.insufficientPermissions", "Insufficient permissions");
  }
  if (noProviders) {
    return t(
      "partners.admin.noAvailableProviders",
      "No more providers available",
    );
  }
  return undefined;
};

const buildSavePayload = (values: PartnerProviderFormValues) => {
  const apiKey = values.apiKey?.trim();
  const webhookSecret = values.webhookSecret?.trim();
  const description = values.description?.trim();
  const categories = values.categories?.trim();

  return {
    apiKey: apiKey || undefined,
    baseUrl: values.baseUrl.trim(),
    syncIntervalMinutes: values.syncIntervalMinutes,
    platformFeePercent: values.platformFeePercent,
    webhookSecret: webhookSecret || undefined,
    metadata:
      description || categories
        ? {
            ...(description ? { description } : {}),
            ...(categories ? { categories } : {}),
          }
        : undefined,
  };
};

export const PartnersPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("partners");
  const { data: partners = [], isLoading, isError } = usePartnerProviders();
  const savePartnerProvider = useSavePartnerProvider();

  const [isCreateOpen, setCreateOpen] = React.useState(false);
  const [editingPartner, setEditingPartner] =
    React.useState<AdminPartnerProviderConfiguration | null>(null);

  const configuredProviders = React.useMemo(
    () => new Set(partners.map((partner) => partner.provider)),
    [partners],
  );

  const availableProviders = React.useMemo(
    () =>
      SUPPORTED_PARTNER_PROVIDERS.filter(
        (provider) => !configuredProviders.has(provider),
      ),
    [configuredProviders],
  );

  const handleSave = async (values: PartnerProviderFormValues) => {
    await savePartnerProvider.mutateAsync({
      provider: values.provider,
      enabled: values.enabled,
      data: buildSavePayload(values),
    });
    toast.success(t("partners.admin.toasts.saveSuccess"));
    setCreateOpen(false);
    setEditingPartner(null);
  };

  const columns: Column<AdminPartnerProviderConfiguration>[] = [
    {
      key: "provider",
      header: t("partners.admin.table.provider"),
      render: (value: string) => (
        <span className="text-foreground font-medium">
          {t(`partners.providers.${value}`, { defaultValue: value })}
        </span>
      ),
    },
    {
      key: "enabled",
      header: t("partners.admin.table.enabled"),
      render: (value: boolean) => (
        <Badge variant={value ? "success" : "secondary"}>
          {value
            ? t("partners.admin.status.enabled")
            : t("partners.admin.status.disabled")}
        </Badge>
      ),
    },
    {
      key: "syncIntervalMinutes",
      header: t("partners.admin.table.syncIntervalMinutes"),
    },
    {
      key: "platformFeePercent",
      header: t("partners.admin.table.platformFeePercent"),
      render: (value: number) => `${value}%`,
    },
    {
      key: "metadata.description",
      header: t("partners.admin.table.description"),
      className: "min-w-[320px] whitespace-normal",
      render: (value: string | undefined) =>
        value ? (
          <p className="max-w-md leading-5 break-words whitespace-normal">
            {value}
          </p>
        ) : (
          "—"
        ),
    },
    {
      key: "metadata.categories",
      header: t("partners.admin.table.categories"),
      className: "min-w-[220px] whitespace-normal",
      render: (value: string | undefined) => {
        const categories = value
          ?.split(",")
          .map((category) => category.trim())
          .filter(Boolean);

        if (!categories?.length) {
          return "—";
        }

        return (
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {categories.map((category) => (
              <li key={category} className="leading-5 break-words">
                {category}
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      key: "hasApiKey",
      header: t("partners.admin.table.hasApiKey"),
      render: (value: boolean) => (
        <Badge variant={value ? "success" : "secondary"}>
          {value
            ? t("partners.admin.boolean.yes")
            : t("partners.admin.boolean.no")}
        </Badge>
      ),
    },
    {
      key: "hasWebhookSecret",
      header: t("partners.admin.table.hasWebhookSecret"),
      render: (value: boolean) => (
        <Badge variant={value ? "success" : "secondary"}>
          {value
            ? t("partners.admin.boolean.yes")
            : t("partners.admin.boolean.no")}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: t("partners.admin.table.actions"),
      className: "w-[88px] text-right",
      render: (_value: unknown, row) => (
        <div
          className="flex items-center justify-end"
          onClick={(event) => event.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            type="button"
            disabled={!canWrite}
            onClick={() => setEditingPartner(row)}
            aria-label={t("partners.admin.table.edit")}
            title={t("partners.admin.table.edit")}
          >
            <Pencil className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <PageState text={t("partners.admin.loading")} />;
  }

  if (isError) {
    return <PageState text={t("partners.admin.loadError")} tone="error" />;
  }

  return (
    <div className="mx-auto flex flex-col gap-6 p-12">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-foreground text-2xl font-semibold">
            {t("partners.admin.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("partners.admin.description")}
          </p>
        </div>

        <Button
          variant="icon-text"
          disabled={!canWrite || availableProviders.length === 0}
          onClick={() => setCreateOpen(true)}
          title={getAddButtonTitle(
            !canWrite,
            availableProviders.length === 0,
            t,
          )}
        >
          <Plus className="size-4" />
          {t("partners.admin.add")}
        </Button>
      </div>

      <DataTable
        data={partners}
        columns={columns}
        emptyMessage={t("partners.admin.table.empty")}
        getRowKey={(row) => row.id}
        withPagination={false}
        withSorting={false}
      />

      <PartnerProviderDialog
        open={isCreateOpen}
        onOpenChange={setCreateOpen}
        availableProviders={[...availableProviders]}
        isPending={savePartnerProvider.isPending}
        onSubmit={async (values) => {
          try {
            await handleSave(values);
          } catch {
            toast.error(t("partners.admin.toasts.saveError"));
          }
        }}
      />

      <PartnerProviderDialog
        open={!!editingPartner}
        onOpenChange={(open) => {
          if (!open) {
            setEditingPartner(null);
          }
        }}
        partner={editingPartner}
        availableProviders={[]}
        isPending={savePartnerProvider.isPending}
        onSubmit={async (values) => {
          try {
            await handleSave(values);
          } catch {
            toast.error(t("partners.admin.toasts.saveError"));
          }
        }}
      />
    </div>
  );
};
